/**
 * Notifications log API client surface (any authenticated user):
 *   GET   /api/user/notifications?limit&unseen_only&before_id  - recent list / history page
 *   GET   /api/user/notifications/unread-count        - badge poll
 *   PATCH /api/user/notifications/{id}                - mark one seen
 *   POST  /api/user/notifications/mark-all-seen       - mark all seen
 *
 * `apiClient` baseURL is `/api`, so paths are `/user/notifications...`.
 * The list param-name mapping (unseenOnly -> unseen_only, beforeId ->
 * before_id) is the seam the hook tests assert. `before_id` is the keyset
 * cursor the history drawer pages with.
 */

import { apiClient } from './api'
import type { AppNotification } from '../types/notifications'

export interface ListNotificationsParams {
  limit?: number
  unseenOnly?: boolean
  /** Keyset cursor: id of the last row already loaded; returns the next older page. */
  beforeId?: number
}

/**
 * Pure optimistic transforms shared by the bell (`useNotifications`) and the
 * history drawer (`useNotificationHistory`) so the "stamp seen_at locally
 * before the round-trip" logic lives in one place.
 */
export function markOneSeenInList(
  list: AppNotification[],
  id: number,
  at: string,
): AppNotification[] {
  return list.map((n) => (n.id === id && !n.seen_at ? { ...n, seen_at: at } : n))
}

export function markAllSeenInList(
  list: AppNotification[],
  at: string,
): AppNotification[] {
  return list.map((n) => (n.seen_at ? n : { ...n, seen_at: at }))
}

export const notificationsApi = {
  async list({
    limit = 50,
    unseenOnly = false,
    beforeId,
  }: ListNotificationsParams = {}): Promise<AppNotification[]> {
    const response = await apiClient.get<AppNotification[]>('/user/notifications', {
      params: { limit, unseen_only: unseenOnly, before_id: beforeId },
    })
    return response.data
  },

  async unreadCount(): Promise<number> {
    const response = await apiClient.get<{ unread: number }>(
      '/user/notifications/unread-count',
    )
    return response.data.unread
  },

  async markSeen(id: number): Promise<AppNotification> {
    const response = await apiClient.patch<AppNotification>(`/user/notifications/${id}`)
    return response.data
  },

  async markAllSeen(): Promise<number> {
    const response = await apiClient.post<{ updated: number }>(
      '/user/notifications/mark-all-seen',
    )
    return response.data.updated
  },
}
